/**
 * Per-project AI Assistant chat history in IndexedDB.
 * Record: { id, projectId, title, messages, createdAt, updatedAt }
 */

const DB_NAME = "cc-assistant";
const DB_VERSION = 1;
const CHAT_STORE = "chats";

let dbPromise = null;

function requestToPromise(request) {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

function openDb() {
    if (!dbPromise) {
        dbPromise = new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);
            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(CHAT_STORE)) {
                    const store = db.createObjectStore(CHAT_STORE, { keyPath: "id" });
                    store.createIndex("projectId", "projectId", { unique: false });
                }
            };
            request.onsuccess = () => {
                const db = request.result;
                db.onversionchange = () => {
                    db.close();
                    dbPromise = null;
                };
                resolve(db);
            };
            request.onerror = () => {
                dbPromise = null;
                reject(request.error);
            };
        });
    }
    return dbPromise;
}

async function withStore(mode, callback) {
    const db = await openDb();
    const tx = db.transaction(CHAT_STORE, mode);
    const done = new Promise((resolve, reject) => {
        tx.oncomplete = resolve;
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
    });
    const [result] = await Promise.all([callback(tx.objectStore(CHAT_STORE)), done]);
    return result;
}

export async function listChats(projectId) {
    if (!projectId) return [];
    return withStore("readonly", (store) =>
        requestToPromise(store.index("projectId").getAll(projectId)),
    );
}

export async function getChat(id) {
    return withStore("readonly", (store) => requestToPromise(store.get(id)));
}

export async function saveChat(chat) {
    // Strip Svelte proxies so the record is structured-cloneable.
    const record = JSON.parse(JSON.stringify(chat));
    await withStore("readwrite", (store) => requestToPromise(store.put(record)));
    return record;
}

export async function deleteChat(id) {
    await withStore("readwrite", (store) => requestToPromise(store.delete(id)));
}
